import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Icon } from "semantic-ui-react";

const ACCENT = "#e03997";

const fieldStyle = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "0.6rem",
    padding: "0.9rem 1rem",
    background: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: "12px",
    cursor: "pointer",
    outline: "none",
};

const labelMini = { fontSize: "0.7rem", color: "#999", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "0.3rem" };
const valueText = { fontSize: "0.95rem", color: "#333", fontWeight: "bold" };

function CategoryPicker({ options, selected, onChange }) {
    const [open, setOpen] = useState(false);
    const [shown, setShown] = useState(false);
    const [temp, setTemp] = useState([]);
    const [query, setQuery] = useState("");
    const [dragY, setDragY] = useState(0);
    const [dragging, setDragging] = useState(false);
    const dragStart = useRef(0);

    const selectedValues = selected || [];

    const openPicker = () => {
        setTemp(selectedValues);
        setQuery("");
        setOpen(true);
        requestAnimationFrame(() => setShown(true));
    };

    const close = () => {
        setShown(false);
        setTimeout(() => setOpen(false), 220);
    };

    const confirm = () => {
        onChange(temp);
        close();
    };

    const toggle = (value) => {
        setTemp((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    };

    const onDragStart = (e) => {
        dragStart.current = e.touches ? e.touches[0].clientY : e.clientY;
        setDragging(true);
        if (e.pointerId !== undefined && e.currentTarget.setPointerCapture) {
            try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
        }
    };
    const onDragMove = (e) => {
        if (!dragging) return;
        const y = e.touches ? e.touches[0].clientY : e.clientY;
        const delta = y - dragStart.current;
        if (delta > 0) setDragY(delta);
    };
    const onDragEnd = () => {
        if (!dragging) return;
        setDragging(false);
        if (dragY > 110) close();
        setDragY(0);
    };

    const count = selectedValues.length;
    const summary = count === 0
        ? "Všetky kategórie"
        : options.filter((o) => selectedValues.includes(o.value)).map((o) => o.text).join(", ");

    const filtered = options.filter((o) => o.text.toLowerCase().includes(query.toLowerCase()));

    return (
        <>
            <button type="button" onClick={openPicker} style={fieldStyle}>
                <Icon name="tags" size="large" style={{ color: ACCENT, margin: 0 }} />
                <div style={{ textAlign: "left", flex: 1, minWidth: 0 }}>
                    <div style={labelMini}>Kategórie{count > 0 ? ` (${count})` : ""}</div>
                    <div style={{ ...valueText, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {summary}
                    </div>
                </div>
                <Icon name="chevron down" style={{ color: "#ccc", margin: 0 }} />
            </button>

            {open && createPortal(
                <div
                    onClick={close}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "rgba(0,0,0,0.45)",
                        zIndex: 100001,
                        opacity: shown ? 1 : 0,
                        transition: "opacity 0.22s ease",
                        display: "flex",
                        alignItems: "flex-end",
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: "100%",
                            maxWidth: "600px",
                            margin: "0 auto",
                            background: "#fff",
                            borderTopLeftRadius: "20px",
                            borderTopRightRadius: "20px",
                            boxShadow: "0 -6px 30px rgba(0,0,0,0.25)",
                            transform: !shown ? "translateY(100%)" : `translateY(${dragY}px)`,
                            transition: dragging ? "none" : "transform 0.22s ease",
                            maxHeight: "88vh",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <div
                            onPointerDown={onDragStart}
                            onPointerMove={onDragMove}
                            onPointerUp={onDragEnd}
                            onPointerCancel={onDragEnd}
                            style={{ flexShrink: 0, cursor: "grab", touchAction: "none", paddingTop: "0.8rem" }}
                        >
                            <div style={{ width: "44px", height: "5px", background: "#e0e0e0", borderRadius: "3px", margin: "0 auto 0.8rem" }} />
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 1.1rem 0.9rem" }}>
                                <span style={{ fontSize: "1.1rem", fontWeight: "bold", color: "#222" }}>Vyber kategórie</span>
                                <button
                                    type="button"
                                    onPointerDown={(e) => { e.stopPropagation(); close(); }}
                                    style={{ width: "40px", height: "40px", flexShrink: 0, borderRadius: "50%", border: "none", background: "#f4f4f6", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                                >
                                    <Icon name="close" style={{ margin: 0, color: "#555" }} />
                                </button>
                            </div>
                        </div>

                        <div style={{ padding: "0 1.1rem 0.8rem", flexShrink: 0 }}>
                            <div style={{ position: "relative" }}>
                                <Icon name="search" style={{ position: "absolute", left: "0.8rem", top: "50%", transform: "translateY(-50%)", color: "#bbb", margin: 0 }} />
                                <input
                                    autoFocus
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Hľadať kategóriu..."
                                    style={{
                                        width: "100%",
                                        padding: "0.7rem 0.9rem 0.7rem 2.4rem",
                                        border: "1px solid #e0e0e0",
                                        borderRadius: "10px",
                                        fontSize: "0.95rem",
                                        outline: "none",
                                    }}
                                />
                            </div>
                        </div>

                        <div style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "0.2rem 0 0.6rem" }}>
                            {filtered.map((o) => {
                                const sel = temp.includes(o.value);
                                return (
                                    <button
                                        key={o.value}
                                        type="button"
                                        onClick={() => toggle(o.value)}
                                        style={{
                                            width: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.8rem",
                                            padding: "0.85rem 1.1rem",
                                            border: "none",
                                            borderBottom: "1px solid #f0f0f0",
                                            background: sel ? "rgba(224, 57, 151, 0.06)" : "#fff",
                                            color: sel ? ACCENT : "#444",
                                            fontSize: "0.95rem",
                                            fontWeight: sel ? "bold" : "normal",
                                            cursor: "pointer",
                                            textAlign: "left",
                                        }}
                                    >
                                        <span
                                            style={{
                                                width: "22px",
                                                height: "22px",
                                                flexShrink: 0,
                                                borderRadius: "6px",
                                                border: sel ? `none` : "2px solid #ccc",
                                                background: sel ? ACCENT : "#fff",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            {sel && <Icon name="check" style={{ margin: 0, fontSize: "0.75rem", color: "#fff" }} />}
                                        </span>
                                        {o.text}
                                    </button>
                                );
                            })}
                            {filtered.length === 0 && (
                                <span style={{ color: "#999", fontSize: "0.9rem", padding: "1rem 1.1rem", display: "block" }}>Nič sa nenašlo</span>
                            )}
                        </div>

                        <div style={{ padding: "0.9rem 1.1rem calc(1.1rem + env(safe-area-inset-bottom))", borderTop: "1px solid #eee", flexShrink: 0, display: "flex", gap: "0.6rem", alignItems: "center" }}>
                            {temp.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setTemp([])}
                                    style={{ padding: "1rem 1.1rem", border: "1px solid #e0e0e0", borderRadius: "12px", background: "#fff", color: "#777", fontWeight: "bold", fontSize: "0.95rem", cursor: "pointer", whiteSpace: "nowrap" }}
                                >
                                    Zrušiť
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={confirm}
                                style={{
                                    flex: 1,
                                    padding: "1rem",
                                    border: "none",
                                    borderRadius: "12px",
                                    background: ACCENT,
                                    color: "#fff",
                                    fontWeight: "bold",
                                    fontSize: "1rem",
                                    cursor: "pointer",
                                }}
                            >
                                Potvrdiť{temp.length > 0 ? ` (${temp.length})` : ""}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}

export default CategoryPicker;
